import { Intent, IntentStatus } from '@futrli-sdk/billing';
import { keys, pick } from 'lodash';
import { ReactStripeElements } from 'react-stripe-elements';

import { IntentActions } from '../core/types';

/**
 * Handles the setup intent functionality.
 * @param {IntentActions} intentActions
 * @param {ReactStripeElements.StripeProps} stripe
 * @param {stripe.BillingDetails} billingDetails
 * @returns {string|stripe.Error|undefined}
 */
export async function handleSetupIntent(
  intentActions: IntentActions,
  stripe: ReactStripeElements.StripeProps,
  billingDetails: stripe.BillingDetails
): Promise<string|stripe.Error|undefined> {
  let setupIntent = await intentActions.createIntent();

  if (!setupIntent) {
    return;
  }

  const response: stripe.SetupIntentResponse = await stripe.handleCardSetup(
    setupIntent.client_secret,
    {
      payment_method_data: {
        billing_details: billingDetails
      }
    }
  );

  if (response.error) {
    return response.error;
  }

  setupIntent = formatStripeIntentToIntent(setupIntent, response.setupIntent);

  return handleIntent(setupIntent, intentActions, stripe);
}

/**
 * Handles the payment intent functionality.
 * @param {IntentActions} intentActions
 * @param {ReactStripeElements.StripeProps} stripe
 * @param {string} paymentMethodId
 * @returns {string|stripe.Error|undefined}
 */
export async function handlePaymentIntent(
  intentActions: IntentActions,
  stripe: ReactStripeElements.StripeProps,
  paymentMethodId?: string
): Promise<string|stripe.Error|undefined> {
  const paymentIntent = await intentActions.createIntent(paymentMethodId);

  if (!paymentIntent) {
    return;
  }

  return handleIntent(paymentIntent, intentActions, stripe, paymentMethodId);
}

/**
 * Handles the intent after being created. This method gets a payment/setup intent and
 * handles it depending on the intent status.
 * @param {Intent} intent
 * @param {IntentActions} intentActions
 * @param {ReactStripeElements.StripeProps} stripe
 * @param {string} paymentMethodId
 * @returns {string|stripe.Error|undefined}
 */
export async function handleIntent(
  intent: Intent,
  intentActions: IntentActions,
  stripe: ReactStripeElements.StripeProps,
  paymentMethodId?: string
): Promise<string|stripe.Error|undefined> {

  switch (intent.status) {
    case IntentStatus.requires_action: {
      const response = await stripe.handleCardAction(intent.client_secret);

      if (response.error) {
        return response.error;
      }

      const updatedIntent = await intentActions.handleIntent(intent.id);

      if (!updatedIntent) {
        return;
      }

      return updatedIntent.id;
    }
    case IntentStatus.requires_confirmation: {
      const confirmedPayment = await stripe.handleCardPayment(intent.client_secret, {
        payment_method: paymentMethodId
      });

      if (confirmedPayment.error) {
        return confirmedPayment.error;
      }

      const confirmedIntent = formatStripeIntentToIntent(intent, confirmedPayment.paymentIntent);

      return handleIntent(confirmedIntent, intentActions, stripe);
    }
    case IntentStatus.succeeded: {
      const succeededIntent = await intentActions.handleIntent(intent.id);

      if (!succeededIntent) {
        return;
      }

      return succeededIntent.id;
    }
    default:
      return intent.id;
  }
}

/**
 * Formats the setup/payment intent from stripe sdk into our Intent format.
 * @param {Intent} intent
 * @param {stripe.setupIntents.SetupIntent|stripe.paymentIntents.PaymentIntent} stripeIntent
 * @returns {Intent}
 */
export function formatStripeIntentToIntent(
  intent: Intent,
  stripeIntent?: stripe.setupIntents.SetupIntent|stripe.paymentIntents.PaymentIntent
): Intent {
  return Object.assign({}, intent, pick(stripeIntent, keys(intent)));
}
